import React from 'react';
import { useTranslation } from 'react-i18next';

import { createCampaign, getAffiliateError, updateCampaign } from '../api';
import { toAffiliateCampaignInput, type LumioRow } from '../lumio-import';

export interface LumioImportOptions {
  casinoInstanceId: string | undefined;
  brandBaseUrl: string;
  /** Called after a successful create/update, so the affiliate campaigns list refetches. */
  onApplied: () => void;
  onToast: (message: string) => void;
}

export interface LumioImportResult {
  /** Acquisition campaign_ids whose create/update is in flight (CTA disabled). */
  importingIds: ReadonlySet<string>;
  /** Acquisition campaign_ids created OR updated THIS session (hidden until the reload lands). */
  appliedIds: ReadonlySet<string>;
  apply: (row: LumioRow) => void;
}

/**
 * Owns the one-click apply for a Lumio row: a 'new' row is CREATED as an affiliate campaign, a
 * 'changed' row UPDATES the existing affiliate campaign (matched earlier by external_ref) to the
 * current source values. There is no delete API, so a 'changed' row must update in place rather
 * than re-create (which would duplicate).
 *
 * `appliedIds` exists because after a successful write the affiliate-list reload is still in
 * flight, so the reloaded set does not yet reflect the change; without this session-local set the
 * row would linger and a second click could duplicate it. Refs back the synchronous guard so a
 * rapid double-click cannot race the state update. Everything resets on a brand switch.
 */
export function useLumioImport({
  casinoInstanceId,
  brandBaseUrl,
  onApplied,
  onToast,
}: LumioImportOptions): LumioImportResult {
  const { t } = useTranslation();
  const [importingIds, setImportingIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const [appliedIds, setAppliedIds] = React.useState<ReadonlySet<string>>(() => new Set());
  const inFlightRef = React.useRef<Set<string>>(new Set());
  const appliedRef = React.useRef<Set<string>>(new Set());

  // The in-flight / applied sets describe ONE brand's campaigns; clear them on a brand switch so
  // state can't leak between brands.
  React.useEffect(() => {
    inFlightRef.current = new Set();
    appliedRef.current = new Set();
    setImportingIds(new Set());
    setAppliedIds(new Set());
  }, [casinoInstanceId]);

  const apply = React.useCallback(
    (row: LumioRow): void => {
      const id = row.campaign.campaign_id;
      if (!casinoInstanceId) return;
      if (row.status === 'changed' && !row.existingId) return;
      if (inFlightRef.current.has(id) || appliedRef.current.has(id)) return;

      inFlightRef.current.add(id);
      setImportingIds(new Set(inFlightRef.current));

      const input = toAffiliateCampaignInput(row.campaign, brandBaseUrl);
      const op =
        row.status === 'changed' && row.existingId
          ? updateCampaign(casinoInstanceId, row.existingId, input)
          : createCampaign(casinoInstanceId, input);

      op.then(() => {
        appliedRef.current.add(id);
        setAppliedIds(new Set(appliedRef.current));
        onToast(row.status === 'changed' ? t('Campaign updated') : t('Campaign made available to Affiliates'));
        onApplied();
      })
        .catch((err: unknown) => {
          onToast(getAffiliateError(err).message);
        })
        .finally(() => {
          inFlightRef.current.delete(id);
          setImportingIds(new Set(inFlightRef.current));
        });
    },
    [casinoInstanceId, brandBaseUrl, onApplied, onToast, t],
  );

  return { importingIds, appliedIds, apply };
}
